import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";
import { payoutApi } from "./api/payoutApiClient";
import {
  compactBody,
  errorMessage,
  isPayoutMockMode,
  readSessionJson,
  stableIdempotencyKey,
  verifyStepUp,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import { showPayoutNotification } from "./PayoutNotification";
import { dashboardContext } from "../Dashboard";

type BulkApprovalItem = {
  transferId: string;
  beneficiary: string;
  amount: number;
  account?: string;
  accountDetail?: string;
  requestedBy?: string;
  makerUserId?: string;
  makerEmail?: string;
};

type BulkApprovalDraft = {
  approvals: BulkApprovalItem[];
  totalAmount: number;
  count: number;
};

const emptyDraft: BulkApprovalDraft = {
  approvals: [],
  totalAmount: 0,
  count: 0,
};

const formatINR = (amount: number) =>
  `\u20B9 ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const normalizeIdentity = (value: unknown) =>
  String(value ?? "").trim().toLowerCase();

const getNestedValue = (source: unknown, path: string[]) =>
  path.reduce<unknown>((current, key) => {
    if (!current || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[key];
  }, source);

const decodeJwtPayload = (token: string | null) => {
  if (!token) return {};
  try {
    const [, payload] = token.split(".");
    if (!payload) return {};
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(window.atob(normalized)) as Record<string, unknown>;
  } catch {
    return {};
  }
};

const collectCurrentUserIdentifiers = (dashboardUser: unknown) => {
  const merchant =
    getNestedValue(dashboardUser, ["getMerchantQuery"]) ||
    getNestedValue(dashboardUser, ["getUserQuery"]) ||
    dashboardUser ||
    {};
  const payoutClaims = decodeJwtPayload(window.localStorage.getItem("payoutsAccessToken"));
  const merchantClaims = decodeJwtPayload(
    window.localStorage.getItem("merchantToken") || window.localStorage.getItem("token"),
  );
  const values = [
    getNestedValue(merchant, ["id"]),
    getNestedValue(merchant, ["_id"]),
    getNestedValue(merchant, ["user_id"]),
    getNestedValue(merchant, ["userId"]),
    getNestedValue(merchant, ["email"]),
    getNestedValue(merchant, ["email_id"]),
    getNestedValue(merchant, ["name"]),
    getNestedValue(merchant, ["user"]),
    getNestedValue(payoutClaims, ["id"]),
    getNestedValue(payoutClaims, ["sub"]),
    getNestedValue(payoutClaims, ["user_id"]),
    getNestedValue(payoutClaims, ["userId"]),
    getNestedValue(payoutClaims, ["email"]),
    getNestedValue(merchantClaims, ["id"]),
    getNestedValue(merchantClaims, ["sub"]),
    getNestedValue(merchantClaims, ["user_id"]),
    getNestedValue(merchantClaims, ["userId"]),
    getNestedValue(merchantClaims, ["email"]),
  ];
  return new Set(values.map(normalizeIdentity).filter(Boolean));
};

type BulkApprovalReviewItem = BulkApprovalItem & {
  blockedReasons: string[];
};

function FlowStep({
  index,
  label,
  active,
  complete,
}: {
  index: number;
  label: string;
  active?: boolean;
  complete?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={
          "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold " +
          (complete || active
            ? "bg-[#1B163B] text-white"
            : "border border-[#D6DCEB] bg-white text-[#505E8E]")
        }
      >
        {complete ? <FiCheck /> : index}
      </span>
      <span
        className={
          "text-sm font-semibold " +
          (active ? "text-[#1B163B]" : "text-[#505E8E]")
        }
      >
        {label}
      </span>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-[#E0E5F2] py-3 last:border-b-0">
      <span className="text-sm font-medium text-[#505E8E]">{label}</span>
      <div className="max-w-[220px] text-right text-sm font-semibold text-[#1B163B]">
        {value}
      </div>
    </div>
  );
}

function PayoutBulkApprovalVerify() {
  const navigate = useNavigate();
  const { user: dashboardUser } = React.useContext(dashboardContext) || {};
  const draft = useMemo(
    () => readSessionJson<BulkApprovalDraft>("payoutBulkApprovalDraft", emptyDraft),
    [],
  );
  const currentUserIdentifiers = useMemo(
    () => collectCurrentUserIdentifiers(dashboardUser),
    [dashboardUser],
  );
  const reviewedApprovals = useMemo<BulkApprovalReviewItem[]>(
    () =>
      draft.approvals.map((item) => {
        const blockedReasons: string[] = [];
        if (!String(item.transferId || "").trim() || item.transferId === "-") {
          blockedReasons.push("Missing transfer ID");
        }
        if (!item.amount || item.amount <= 0) {
          blockedReasons.push("Invalid approval amount");
        }
        const makerIdentifiers = [
          item.makerUserId,
          item.makerEmail,
          item.requestedBy,
        ]
          .map(normalizeIdentity)
          .filter(Boolean);
        if (
          makerIdentifiers.length &&
          makerIdentifiers.some((identifier) => currentUserIdentifiers.has(identifier))
        ) {
          blockedReasons.push("Maker cannot approve own payment");
        }
        return { ...item, blockedReasons };
      }),
    [currentUserIdentifiers, draft.approvals],
  );
  const approvableApprovals = useMemo(
    () => reviewedApprovals.filter((item) => item.blockedReasons.length === 0),
    [reviewedApprovals],
  );
  const blockedApprovals = useMemo(
    () => reviewedApprovals.filter((item) => item.blockedReasons.length > 0),
    [reviewedApprovals],
  );
  const approvableTotal = useMemo(
    () => approvableApprovals.reduce((sum, item) => sum + item.amount, 0),
    [approvableApprovals],
  );
  const accountBreakdown = useMemo(() => {
    const totals = new Map<string, { count: number; amount: number; detail: string }>();
    approvableApprovals.forEach((item) => {
      const account = item.account || "Unknown Account";
      const existing = totals.get(account) || { count: 0, amount: 0, detail: item.accountDetail || "" };
      totals.set(account, {
        count: existing.count + 1,
        amount: existing.amount + item.amount,
        detail: existing.detail || item.accountDetail || "",
      });
    });
    return Array.from(totals.entries()).map(([account, value]) => ({
      account,
      ...value,
    }));
  }, [approvableApprovals]);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [, setSubmitError] = useState("");
  const showSubmitError = React.useCallback((message: string) => {
    setSubmitError(message);
    showPayoutNotification({ tone: "error", message });
  }, []);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const code = useMemo(() => otp.join(""), [otp]);
  const ready = code.length === 6 && approvableApprovals.length > 0;

  const updateDigit = (index: number, value: string) => {
    const nextValue = value.replace(/\D/g, "").slice(-1);
    const nextOtp = [...otp];
    nextOtp[index] = nextValue;
    setOtp(nextOtp);

    if (nextValue && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;
    const nextOtp = ["", "", "", "", "", ""];
    digits.forEach((digit, index) => {
      nextOtp[index] = digit;
    });
    setOtp(nextOtp);
    inputsRef.current[Math.min(digits.length, 5)]?.focus();
  };

  const handleVerifyBulkApproval = async () => {
    setSubmitError("");
    if (!ready) {
      showSubmitError(
        approvableApprovals.length
          ? "Bulk approve: enter the 6-digit Google Authenticator code to approve the selected transfers."
          : "Bulk approve: no selected approvals can be submitted. Review the blocked rows.",
      );
      return;
    }
    if (submitting) return;

    if (isPayoutMockMode()) {
      writeSessionJson("payoutBulkApprovalResult", {
        approved: approvableApprovals,
        failed: [],
        blocked: blockedApprovals,
        totalAmount: approvableTotal,
      });
      navigate("/payouts/transfers/pending-approval");
      return;
    }

    setSubmitting(true);
    try {
      const stepUpToken = await verifyStepUp({
        purpose: "BULK_APPROVE_PAYOUT",
        method: "authenticator",
        code,
        subjectId: approvableApprovals.map((item) => item.transferId).join(","),
      });
      const settledResults = await Promise.allSettled(
        approvableApprovals.map(async (item) => {
          const response = await payoutApi.approvePayment(
            item.transferId,
            compactBody({ decision: "APPROVED" }),
            stableIdempotencyKey("bulk-approve-payment", item.transferId),
            stepUpToken,
          );
          return { item, response };
        }),
      );
      const approved: Array<{ item: BulkApprovalReviewItem; response: unknown }> = [];
      const failed: Array<{ item: BulkApprovalReviewItem; reason: string }> = [];
      settledResults.forEach((result, index) => {
        if (result.status === "fulfilled") {
          approved.push(result.value);
          return;
        }
        failed.push({
          item: approvableApprovals[index],
          reason: errorMessage(result.reason, "Approval failed for this transfer."),
        });
      });
      writeSessionJson("payoutBulkApprovalResult", {
        approved,
        failed,
        blocked: blockedApprovals,
        totalAmount: approved.reduce((sum, result) => sum + result.item.amount, 0),
        requestedTotalAmount: draft.totalAmount,
      });
      if (failed.length || blockedApprovals.length) {
        showPayoutNotification({
          tone: "warning",
          message: `${approved.length} approved, ${failed.length} failed, ${blockedApprovals.length} blocked.`,
        });
      } else {
        showPayoutNotification({
          tone: "success",
          message: `${approved.length} payout approvals submitted successfully.`,
        });
      }
      navigate("/payouts/transfers/pending-approval");
    } catch (error) {
      showSubmitError(errorMessage(error, "Bulk approve: unable to approve the selected transfers. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Verify Bulk Approval
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Verify selected payout transfer approvals before submitting them.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-8">
        <FlowStep index={1} label="Select Approvals" complete />
        <span className="h-px w-16 bg-[#D6DCEB]" />
        <FlowStep index={2} label="Verify" active />
        <span className="h-px w-16 bg-[#D6DCEB]" />
        <FlowStep index={3} label="Confirm" />
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,760px)_380px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-6 flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#1B163B0D] text-xl text-[#1B163B]">
              <FiShield />
            </div>
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                Verify Bulk Approval
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                Enter the 6-digit code from your Google Authenticator app.
              </p>
            </div>
          </div>

          <div className="mb-6 flex gap-3 rounded-[8px] border border-[#F59E0B66] bg-[#FEF3C7] px-4 py-3 text-sm font-semibold text-[#92400E]">
            <FiAlertTriangle className="mt-0.5 shrink-0" />
            <span>
              Note of Caution: You are approving {draft.count} payout request
              {draft.count === 1 ? "" : "s"} with a total value of{" "}
              {formatINR(draft.totalAmount)}. {approvableApprovals.length} can be submitted after
              verification and {blockedApprovals.length} blocked row
              {blockedApprovals.length === 1 ? "" : "s"} will be skipped.
            </span>
          </div>

          <div className="mb-6 grid gap-3 md:grid-cols-3">
            <div className="rounded-[8px] border border-[#E0E5F2] bg-white px-4 py-3">
              <p className="text-xs font-semibold text-[#505E8E]">Selected</p>
              <p className="mt-1 text-lg font-semibold text-[#1B163B]">{draft.count}</p>
            </div>
            <div className="rounded-[8px] border border-[#DCFCE7] bg-[#F0FDF4] px-4 py-3">
              <p className="text-xs font-semibold text-[#166534]">Ready to Approve</p>
              <p className="mt-1 text-lg font-semibold text-[#1B163B]">{approvableApprovals.length}</p>
            </div>
            <div className="rounded-[8px] border border-[#FECACA] bg-[#FEF2F2] px-4 py-3">
              <p className="text-xs font-semibold text-[#991B1B]">Blocked</p>
              <p className="mt-1 text-lg font-semibold text-[#1B163B]">{blockedApprovals.length}</p>
            </div>
          </div>

          {blockedApprovals.length > 0 && (
            <div className="mb-6 rounded-[8px] border border-[#FECACA] bg-[#FEF2F2] p-4">
              <h3 className="text-sm font-semibold text-[#991B1B]">
                Blocked rows will not be submitted
              </h3>
              <div className="mt-3 max-h-[150px] space-y-2 overflow-y-auto">
                {blockedApprovals.map((item) => (
                  <div
                    key={item.transferId || item.beneficiary}
                    className="rounded-[6px] bg-white px-3 py-2 text-xs font-semibold text-[#1B163B]"
                  >
                    <span className="block">{item.transferId || "-"}</span>
                    <span className="mt-1 block text-[#991B1B]">
                      {item.blockedReasons.join(", ")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-[8px] border border-[#1B163B] bg-[#1B163B0D] px-5 py-4 text-left">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#DCFCE7] text-[#16A34A]">
                  <FiShield />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-[#1B163B]">
                    Google Authenticator
                  </span>
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    Recommended for bulk approval actions
                  </span>
                </span>
              </span>
              <FiCheck className="text-lg text-[#16A34A]" />
            </div>
          </div>

          <div className="mt-8">
            <label className="mb-3 block text-sm font-semibold text-[#1B163B]">
              Verification Code
            </label>
            <div className="flex flex-wrap gap-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    inputsRef.current[index] = element;
                  }}
                  value={digit}
                  inputMode="numeric"
                  maxLength={1}
                  onPaste={(event) => {
                    event.preventDefault();
                    handlePaste(event.clipboardData.getData("text"));
                  }}
                  onChange={(event) => updateDigit(index, event.target.value)}
                  className="h-14 w-14 rounded-[8px] border border-[#E0E5F2] bg-white text-center text-xl font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                />
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-6">
            <button
              type="button"
              onClick={() => setOtp(["", "", "", "", "", ""])}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1B163B]"
            >
              <FiRefreshCw /> Resend code
            </button>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-[#E0E5F2] pt-5">
            <Link
              to="/payouts/transfers/pending-approval"
              className="flex h-10 min-w-[110px] items-center justify-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-6 text-sm font-semibold text-[#1B163B]"
            >
              <FiArrowLeft /> Cancel
            </Link>
            <button
              type="button"
              onClick={handleVerifyBulkApproval}
              disabled={!ready || submitting}
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className="flex h-10 min-w-[170px] items-center justify-center gap-3 rounded-[6px] px-6 text-sm font-semibold text-white shadow-[0_10px_18px_rgba(27,22,59,0.22)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Approving..." : "Verify & Continue"}
              <FiArrowRight />
            </button>
          </div>
        </section>

        <aside className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <h2 className="mb-4 text-[18px] font-semibold text-[#1B163B]">
            Bulk Approval Summary
          </h2>
          <SummaryRow label="Approvals Selected" value={draft.count} />
          <SummaryRow label="Ready to Approve" value={approvableApprovals.length} />
          <SummaryRow label="Blocked Rows" value={blockedApprovals.length} />
          <SummaryRow label="Total Selected Amount" value={formatINR(draft.totalAmount)} />
          <SummaryRow label="Approval Amount" value={formatINR(approvableTotal)} />
          <SummaryRow label="Verification" value="Google Authenticator" />
          <div className="mt-5 rounded-[8px] bg-[#F6F8FA] p-3">
            <h3 className="mb-3 text-xs font-semibold uppercase text-[#505E8E]">
              Account Breakdown
            </h3>
            <div className="max-h-[160px] space-y-2 overflow-y-auto">
              {accountBreakdown.map((item) => (
                <div
                  key={item.account}
                  className="rounded-[6px] bg-white px-3 py-2 text-xs font-semibold text-[#1B163B]"
                >
                  <span className="block">{item.account}</span>
                  {item.detail && (
                    <span className="mt-1 block text-[#505E8E]">{item.detail}</span>
                  )}
                  <span className="mt-1 block text-[#505E8E]">
                    {item.count} approval{item.count === 1 ? "" : "s"} - {formatINR(item.amount)}
                  </span>
                </div>
              ))}
              {!accountBreakdown.length && (
                <p className="text-xs font-semibold text-[#505E8E]">No approvable accounts.</p>
              )}
            </div>
          </div>
          <div className="mt-5 max-h-[220px] space-y-3 overflow-y-auto rounded-[8px] bg-[#F6F8FA] p-3">
            {reviewedApprovals.map((item) => (
              <div
                key={item.transferId}
                className={
                  "rounded-[6px] bg-white px-3 py-2 text-xs font-semibold " +
                  (item.blockedReasons.length ? "text-[#991B1B]" : "text-[#1B163B]")
                }
              >
                <span className="block">{item.transferId}</span>
                <span className="mt-1 block text-[#505E8E]">
                  {item.beneficiary} - {formatINR(item.amount)}
                </span>
                <span className="mt-1 block text-[#505E8E]">
                  {item.account || "Unknown Account"} | Maker: {item.requestedBy || item.makerUserId || "-"}
                </span>
                {item.blockedReasons.length > 0 && (
                  <span className="mt-1 block text-[#991B1B]">
                    Blocked: {item.blockedReasons.join(", ")}
                  </span>
                )}
              </div>
            ))}
            {!draft.approvals.length && (
              <p className="text-xs font-semibold text-[#505E8E]">
                No approvals selected.
              </p>
            )}
          </div>
        </aside>
      </div>

    </div>
  );
}

export default PayoutBulkApprovalVerify;
